import { TranscribeMode } from '@heidi/schema/ml-scribe';
import { logConsentGateDecision, useQuerySessionById } from '@heidi/session/data';
import { useQueryUser } from '@heidi/workspace/data';
import { useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { patientConsentModalOpenAtom } from '../../patient-consent/patient-consent-modal.atoms';

export const useCheckIsBlockedOnConsent = (sessionId: string) => {
  const { data: session } = useQuerySessionById({
    sessionId,
    select: (data) => {
      if (!data) return undefined;
      return {
        duration: data.duration,
        is_consent: data.is_consent,
        patient_profile: data.patient_profile,
        transcribe_mode: data.transcribe_mode,
      };
    },
  });

  const setPatientConsentModalOpen = useSetAtom(patientConsentModalOpenAtom);
  const { data: userData } = useQueryUser();

  // `undefined` means the /user request has not resolved in this tab yet, so
  // the gate cannot know whether this clinician requires the popup and lets
  // recording proceed. The log below reports it so the case is observable.
  const settingsLoaded = userData?.session_settings !== undefined;

  const shouldBlockRecordingWithoutConsent =
    Boolean(userData?.session_settings?.is_consent_pop_up) &&
    session?.transcribe_mode !== TranscribeMode.DICTATE &&
    !session?.duration &&
    !session?.is_consent &&
    !session?.patient_profile?.remember_consent;

  return useCallback(() => {
    logConsentGateDecision({
      gate: 'recording_v2',
      sessionId,
      settingsLoaded,
      isConsentPopUpSetting: userData?.session_settings?.is_consent_pop_up,
      sessionIsConsent: session?.is_consent,
      sessionDuration: session?.duration,
      rememberConsent: session?.patient_profile?.remember_consent,
      transcribeMode: session?.transcribe_mode,
      willShowConsentPopup: shouldBlockRecordingWithoutConsent,
    });

    if (shouldBlockRecordingWithoutConsent) {
      setPatientConsentModalOpen(true);
      return true;
    }

    return false;
  }, [
    sessionId,
    settingsLoaded,
    userData?.session_settings?.is_consent_pop_up,
    session?.is_consent,
    session?.duration,
    session?.patient_profile?.remember_consent,
    session?.transcribe_mode,
    shouldBlockRecordingWithoutConsent,
    setPatientConsentModalOpen,
  ]);
};
